import type { ListedConnector } from "../../../api/connectorsApi";
import type { OfferedModel, ReportedProfile, RouteUnavailability } from "../../../api/connectorStatus";
import type { AgentRoute, ApiKeyRoute, ThreadSettings } from "../../../api/threads/liveMessages";
import type { CapabilityGap, CapabilityValue, RouteOption } from "../../../api/threads/routeOptions";

/**
 * What a host can run a Thread on, from a connector's latest capability report
 * (connector.md CN-4): each agent and API route with its models, and whether it is usable
 * now. Whether a model has what the Thread's turns require comes from the server's route
 * options (Q-63, `routeFitOf`), which apply the same rule as preparing a turn (T-4).
 */
export interface RouteChoice {
  /** Unique within the connector: `agent:<id>` or `api:<id>`. */
  readonly key: string;
  readonly kind: "agent" | "api_key";
  readonly id: string;
  readonly title: string;
  readonly models: readonly OfferedModel[];
  /** `null` when usable; else why not, as the server decides it (CP-11). */
  readonly unavailableBecause: RouteUnavailability | null;
}

export type TurnRoute = AgentRoute | ApiKeyRoute;

export interface HostingChoice {
  readonly connectorId: string;
  /** `null`: no agent step is left to run, so the Thread goes live with no route (the server's `needs_route`); its own route stays its setting. */
  readonly route: TurnRoute | null;
  readonly permissionProfile: string;
  /** Routes a turn may fall back to, in order, when its own has a refused key or no credit (Q-93, Q-96). */
  readonly fallbackRoutes: readonly TurnRoute[];
  /** The server's route options say the default memory tools come off this route (memory.md MEM-3). */
  readonly dropsDefaultReplyTools: boolean;
}

/** The server's limit on fallback routes (`MAXIMUM_FALLBACK_ROUTES`). */
export const MAXIMUM_FALLBACK_ROUTES = 8;

/** The Thread's settings with the host's choice, or `null` when the choice changes none of them. */
export function settingsWithChoice(settings: ThreadSettings, choice: HostingChoice): ThreadSettings | null {
  const fallbackRoutes = [...choice.fallbackRoutes];
  const dropsTools = choice.dropsDefaultReplyTools;
  const route = choice.route ?? settings.route;
  const unchanged =
    !dropsTools &&
    sameRoute(settings.route, route) &&
    settings.permission_profile === choice.permissionProfile &&
    JSON.stringify(settings.fallback_routes ?? []) === JSON.stringify(fallbackRoutes);
  // The generated type spells the server's limit as tuples; `withFallback` keeps within it.
  if (unchanged) return null;
  return {
    ...settings,
    route,
    permission_profile: choice.permissionProfile,
    fallback_routes: fallbackRoutes as ThreadSettings["fallback_routes"],
    reply_tools: dropsTools ? { ...settings.reply_tools, dmw: [] } : settings.reply_tools,
  };
}

/** Add a fallback at the end, unless it is the Thread's own route, already listed, or the list is full. */
export function withFallback(fallbacks: readonly TurnRoute[], own: TurnRoute | null, added: TurnRoute): readonly TurnRoute[] {
  if (fallbacks.length >= MAXIMUM_FALLBACK_ROUTES || sameRoute(own, added) || fallbacks.some((route) => sameRoute(route, added))) {
    return fallbacks;
  }
  return [...fallbacks, added];
}

/** Move one fallback up (`-1`) or down (`+1`); the order is the order they are tried in. */
export function movedFallback(fallbacks: readonly TurnRoute[], index: number, step: -1 | 1): readonly TurnRoute[] {
  const target = index + step;
  if (index < 0 || index >= fallbacks.length || target < 0 || target >= fallbacks.length) return fallbacks;
  const moved = [...fallbacks];
  const [route] = moved.splice(index, 1);
  if (route !== undefined) moved.splice(target, 0, route);
  return moved;
}

export function onlineConnectors(connectors: readonly ListedConnector[]): ListedConnector[] {
  return connectors.filter((connector) => connector.state === "active" && connector.status.online);
}

export function routeChoicesOf(connector: ListedConnector): RouteChoice[] {
  const agents: RouteChoice[] = connector.status.agents.map((agent) => ({
    key: `agent:${agent.agentId}`,
    kind: "agent",
    id: agent.agentId,
    // Several configured agents may run the same program, so the ID tells them apart.
    title: agent.title === null || agent.title === agent.agentId ? agent.agentId : `${agent.title} (${agent.agentId})`,
    models: agent.models,
    unavailableBecause: agent.unavailableBecause,
  }));
  const apiRoutes: RouteChoice[] = connector.status.apiRoutes.map((route) => ({
    key: `api:${route.routeId}`,
    kind: "api_key",
    id: route.routeId,
    title: `${route.routeId} (${route.baseUrlHost})`,
    models: route.models,
    unavailableBecause: route.unavailableBecause,
  }));
  return [...agents, ...apiRoutes];
}

/** The route to store; an agent may keep its own default model (`null`), an API may not. */
export function routeOf(choice: RouteChoice, modelId: string | null): TurnRoute | null {
  if (choice.kind === "agent") {
    return { kind: "agent", agent_id: choice.id, model_id: modelId };
  }
  return modelId === null ? null : { kind: "api_key", route_id: choice.id, model_id: modelId };
}

export function sameRoute(left: AgentRoute | ApiKeyRoute | null, right: AgentRoute | ApiKeyRoute | null): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

/** A capability one kind of turn lacks (`no`) or may lack (`unknown`) on a route. */
export interface TurnGap extends CapabilityGap {
  /** `null` stands for reply turns. */
  readonly stepKey: string | null;
  /** For an unknown capability, the estimate an agent reported, if any (D17). */
  readonly hint?: CapabilityValue | null;
}

/**
 * How a route fits the Thread's turns: `unchecked` before the server's answer or for a
 * route it does not know; `uncertain` when some requirement is only unknown, which warns
 * and does not block (D17); `lacks` when the route is unavailable or a turn lacks a
 * capability, naming each turn's gaps, unknown ones too.
 */
export type RouteFit =
  | { readonly kind: "unchecked" }
  | { readonly kind: "fits" }
  | { readonly kind: "uncertain"; readonly gaps: readonly TurnGap[] }
  | { readonly kind: "lacks"; readonly gaps: readonly TurnGap[] };

/** The server's option for a route, or `undefined` before its answer or for a route it does not know. */
export function optionOf(options: readonly RouteOption[] | null, route: AgentRoute | ApiKeyRoute | null): RouteOption | undefined {
  return route === null ? undefined : options?.find((candidate) => sameRoute(candidate.route, route));
}

/**
 * The server's fits as they are: whether the route is blocked is its `runsEveryTurn`, and the
 * gaps say why; where the default reply tools come off a route, the server matched the replies
 * without them (`dropsDefaultReplyTools`), so no gap is the browser's to drop.
 */
export function routeFitOf(options: readonly RouteOption[] | null, route: AgentRoute | ApiKeyRoute | null): RouteFit {
  const option = optionOf(options, route);
  if (option === undefined) {
    return { kind: "unchecked" };
  }
  const gaps = option.fits.flatMap((fit) =>
    fit.gaps
      .map((gap) => ({ ...gap, stepKey: fit.stepKey, hint: fit.requirements.find((requirement) => requirement.capability === gap.capability)?.hint ?? null })),
  );
  if (!option.runsEveryTurn) {
    return { kind: "lacks", gaps };
  }
  return gaps.length === 0 ? { kind: "fits" } : { kind: "uncertain", gaps };
}

/** Whether reviewed tools may read the profile's workspace (Q-81). */
export function opensWorkspaceToTools(profile: ReportedProfile | undefined): boolean {
  return profile !== undefined && profile.itemToolsWorkspace !== "none";
}
